import React from "react";
import {
    Box,
    IconButton,
    OutlinedInput,
    InputLabel,
    MenuItem,
    FormControl,
    Select,
    SelectChangeEvent,
} from "@mui/material";

import { PageContainer } from "./PageContainer";
import { useAppDispatch, useAppSelector } from "#store";
import { H1 } from "#common/headings";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { idRecsService, sql2nl } from "#services/pilotService";
import { ControlledCommandLine } from "../commmandLine/ControlledCommandLine";
import { ActionType, DatasourceEnum } from "#services/pilotService/types";
import { push } from "redux-first-history";

import { generatePath } from "react-router";
import { generateUrl } from "#components/App/routing/generateUrl";
import { Routes } from "#/config";
import RefreshIcon from "@mui/icons-material/Refresh";

interface StringMap {
    [key: string]: string;
}

interface StringMapArray {
    [key: string]: any[];
}

interface StringMapArrayStrings {
    [key: string]: string[];
}

export const SqlSearchComponent: React.FunctionComponent = () => {
    const [query, setQuery] = React.useState<string>("");
    const [sqlQuery, setSqlQuery] = React.useState<string>("");

    const dispatch = useAppDispatch();
    const state = useAppSelector(state => state);
    const datasourceDto = useAppSelector(
        state => state.uiState.selectedDatasource
    );
    React.useEffect(() => {
        getTableRecommendations([]);
    }, [datasourceDto]);

    const currentDatasource = datasourceDto.name;

    const [tables, setTables] = React.useState<string[]>([]);
    const [selectedTables, setSelectedTables] = React.useState<string[]>([]);

    const [joins, setJoins] = React.useState<StringMap>({});

    const [columns, setColumns] = React.useState<string[]>([]);
    const [selectedColumns, setSelectedColumns] = React.useState<string[]>([]);

    const [filters, setFilters] = React.useState<StringMapArray>({});
    const [selectedFilters, setSelectedFilters] =
        React.useState<StringMapArrayStrings>({});

    const handleTableSelection = (
        event: React.MouseEvent<HTMLElement>,
        newTables: string[]
    ) => {
        let table = newTables.filter(x => !selectedTables.includes(x));
        let reward = -1;

        if (table.length > 0) {
            // console.log("selected " + table[0]);
            reward = 1;
        } else {
            table = selectedTables.filter(x => !newTables.includes(x));
            // console.log("deselected", table[0]);
        }

        setSelectedTables(newTables);

        constructQuery(newTables, []);

        if (newTables.length > 0) {
            getTableRecommendations(newTables);
            getColumnRecommendations(newTables);
            getPredRecommendations(newTables);
        } else {
            hideAttrsAndFilters();
        }

        idRecsService(state).update("from", table[0], reward);
    };

    const handleColumnSelection = (
        event: React.MouseEvent<HTMLElement>,
        newColumns: string[]
    ) => {
        setSelectedColumns(newColumns);
        constructQuery(selectedTables, newColumns);

        let column = newColumns.filter(x => !selectedColumns.includes(x));
        let reward = -1;

        if (column.length > 0) {
            // console.log("selected " + column[0]);
            reward = 1;
        } else {
            column = selectedColumns.filter(x => !newColumns.includes(x));
            // console.log("deselected", column[0]);
        }
        idRecsService(state).update("select", column[0], reward);
    };

    const handlePredSelection = (
        selections: string | string[],
        col: string
    ) => {
        selections =
            typeof selections === "string" ? selections.split(",") : selections;

        let newFilter = selections.filter(
            x => !selectedFilters[col].includes(x)
        );
        const s = {
            ...selectedFilters,
            [col]: selections,
        };

        setSelectedFilters(s);
        constructQuery(selectedTables, selectedColumns, s);
        if (newFilter.length == 1) {
            idRecsService(state).update("predicate", col, 1, newFilter[0]);
        }
    };

    const hideAttrsAndFilters = () => {
        setSelectedColumns([]);
        setColumns([]);

        setFilters({});
        setSelectedFilters({});

        setQuery("");
        setSqlQuery("");
    };

    const getTableRecommendations = async (pool: string[]) => {
        hideAttrsAndFilters();

        let pool_type: string | null = null;
        if (pool.length > 0) {
            pool_type = "joins";
        } else {
            setSelectedTables([]);
        }

        idRecsService(state)
            .getRecommendations("from", pool_type, pool)
            .then(result => {
                // const tables = pool.concat(result.recommendations);

                if (pool_type == "joins" && pool.length > 1) {
                    // remove items from the pool that are no longer in the recommendations
                    pool.forEach((table, i) => {
                        if (!result.recommendations.includes(table)) {
                            pool.splice(i, 1);
                        }
                        setSelectedTables(pool);
                    });
                }

                setTables(result.recommendations);
                setJoins(result.joins);
            })
            .catch(error => {
                console.log(error);
            });
    };

    const getColumnRecommendations = async (pool: string[]) => {
        setSelectedColumns([]);
        setColumns([]);

        constructQuery(selectedTables, []);

        idRecsService(state)
            .getRecommendations("select", "tables", pool)
            .then(result => {
                setColumns(result.recommendations);
            })
            .catch(error => {
                console.log(error);
            });
    };

    const getPredRecommendations = async (pool: string[]) => {
        setSelectedFilters({});
        // setSelectedFilters({
        //     "projects.end_year": [],
        //     "projects.framework_program": [],
        //     "projects.ec_fund_scheme": [],
        // });
        setFilters({});
        // setFilters({
        //     "projects.end_year": [2021, 2014, 2013],
        //     "projects.framework_program": ["FP7", "H2020", "CIP"],
        //     "projects.ec_fund_scheme": ["CP-FP", "ERC-STG", "CSA"],
        // });

        idRecsService(state)
            .getPredicates(pool)
            .then(result => {
                let selected: StringMapArray = {};
                setFilters(result.recommendations);
                Object.keys(result.recommendations).forEach(
                    item => (selected[item] = [])
                );
                setSelectedFilters(selected);
            })
            .catch(error => {
                console.log(error);
            });

        // console.log(filters);
        // console.log(selectedFilters);
    };

    const constructQuery = (
        tables: string[],
        columns: string[],
        filters: StringMapArrayStrings = {}
    ) => {
        if (tables.length == 0) return "";

        let sql = "SELECT ";
        if (columns.length > 0) {
            sql += columns.join(", ");
        } else {
            sql += "*";
        }
        sql += " FROM " + tables[0];

        if (tables.length > 1) {
            tables
                .slice(1)
                .forEach(
                    table => (sql += " JOIN " + table + " ON " + joins[table])
                );
        }

        let conditions: string[] = [];
        Object.entries(filters).map(([key, value]) => {
            let values: string[] = [];
            if (value.length > 0) {
                value.forEach(v => {
                    if (isNaN(+v)) {
                        values.push("'" + v + "'");
                    } else values.push(v);
                });
                conditions.push(key + " IN (" + values.join(",") + ")");
            }
        });
        if (conditions.length > 0) sql += " WHERE " + conditions.join(" AND ");

        // console.log(sql);

        setSqlQuery(sql);

        if (sql == "") {
            setQuery("");
        } else {
            sql2nl(state)
                .translateSQL(sql)
                .then(result => {
                    setQuery(result);
                })
                .catch(error => {
                    console.log(error);
                });
        }
        return sql;
    };

    let tablesBox;
    if (tables.length > 0) {
        tablesBox = (
            <Box mb={1} ml={1}>
                {" "}
                Tables:
                <ToggleButtonGroup
                    value={selectedTables}
                    onChange={handleTableSelection}
                    aria-label="text formatting"
                    size="small"
                >
                    {tables.map(x => (
                        <ToggleButton key={x} value={x}>
                            {x}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <IconButton
                    aria-label="refresh"
                    color="primary"
                    onClick={() => getTableRecommendations([])}
                >
                    <RefreshIcon />
                </IconButton>
            </Box>
        );
    }
    let columnsBox;
    if (columns.length > 0) {
        columnsBox = (
            <Box mb={1} ml={1}>
                {" "}
                Attributes:
                <ToggleButtonGroup
                    value={selectedColumns}
                    onChange={handleColumnSelection}
                    aria-label="text formatting"
                    size="small"
                >
                    {columns.map(x => (
                        <ToggleButton key={x} value={x}>
                            {x}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <IconButton
                    aria-label="refresh"
                    color="primary"
                    onClick={() => getColumnRecommendations(selectedTables)}
                >
                    <RefreshIcon />
                </IconButton>
            </Box>
        );
    }

    let predBox;
    if (Object.keys(filters).length > 0) {
        predBox = (
            <Box mb={1} ml={1}>
                {" "}
                Filters:
                {Object.entries(filters).map(([key, value]) => (
                    <FormControl
                        sx={{ m: 1, minWidth: 300 }}
                        size="small"
                        key={key}
                    >
                        <InputLabel>{key}</InputLabel>
                        <Select
                            multiple
                            value={selectedFilters[key]}
                            label="Predicate"
                            onChange={event => {
                                handlePredSelection(event.target.value, key);
                            }}
                            key={key}
                            input={<OutlinedInput label={key} />}
                        >
                            {value.map((x, index) => (
                                <MenuItem key={index} value={x}>
                                    {x}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ))}
                <IconButton
                    aria-label="refresh"
                    color="primary"
                    onClick={() => getPredRecommendations(selectedTables)}
                >
                    <RefreshIcon />
                </IconButton>
            </Box>
        );
    }

    const onSubmit = (
        nlQuery: string,
        sqlQuery: string,
        datasource: DatasourceEnum = currentDatasource
    ) => {
        let q = "";
        let op = ActionType.QL2DATA;
        if (nlQuery.toLowerCase().startsWith("select ")) {
            q = nlQuery;
        } else if (sqlQuery == "") {
            q = nlQuery;
            op = ActionType.NL2SQL;
        } else {
            q = sqlQuery;
        }
        // dispatch(setQuery(q));
        dispatch(
            push(
                generateUrl(generatePath(Routes.triggerAction.path), {
                    q: q,
                    op: op,
                    o: datasourceDto.queryLanguage,
                    db: datasource,
                })
            )
        );
    };
    return (
        <>
            <ControlledCommandLine
                value={query}
                onChange={value => {
                    setSqlQuery("");
                    setQuery(value);
                }}
                error={false}
                onSubmit={v => onSubmit(query, sqlQuery, currentDatasource)}
            />
            <br />

            {tablesBox}
            {columnsBox}
            {predBox}
        </>
    );
};

export const SqlSearchPage = () => {
    return (
        <PageContainer>
            <H1>Query Builder</H1>
            <SqlSearchComponent />
        </PageContainer>
    );
};
